import { DateTime } from 'luxon'
import { parseDate } from './index'
import type Event from '@/types/Event'
import type TicketBatch from '@/types/TicketBatch'

export interface TimelineSpan {
  start: DateTime
  end: DateTime
}

export interface TimelineBar {
  batch: TicketBatch
  start: DateTime
  /** When it closed, or will. Null when open-ended. */
  end: DateTime | null
  /** The configured period, drawn only where the batch did not run to plan. */
  ghost: TimelineSpan | null
  openEnded: boolean
  /** Wholly ahead of now, so it is plan rather than history. */
  future: boolean
}

export interface TimelineModel {
  /** Null when the event carries no usable sales period; nothing is drawable. */
  track: TimelineSpan | null
  now: DateTime
  bars: TimelineBar[]
  /** Stretches inside the track where no batch is sellable. */
  gaps: TimelineSpan[]
}

export function buildTimeline(
  batches: TicketBatch[],
  event: Event,
  now: DateTime = DateTime.now(),
): TimelineModel {
  const sorted = [ ...batches ].sort((a, b) => a.number - b.number)
  const track = trackFor(sorted, event)

  if (!track) {
    return { track: null, now, bars: [], gaps: [] }
  }

  const bars = layout(sorted, track, now)

  return { track, now, bars, gaps: gapsIn(bars, track) }
}

/** Position of an instant on the track, as a percentage. */
export function positionIn(track: TimelineSpan, at: DateTime): number {
  const total = track.end.toMillis() - track.start.toMillis()

  if (total <= 0) return 0

  const offset = ((at.toMillis() - track.start.toMillis()) / total) * 100

  return Math.min(Math.max(offset, 0), 100)
}

export function spanStyle(track: TimelineSpan, start: DateTime, end: DateTime): Record<string, string> {
  const left = positionIn(track, start)

  return { left: `${left}%`, width: `${Math.max(positionIn(track, end) - left, 0)}%` }
}

/**
 * The event's own sales period, widened to cover any batch date outside it
 * (legacy and backfilled rows can sit outside). Null only when the event carries
 * no usable period, which leaves nothing drawable.
 */
function trackFor(sorted: TicketBatch[], event: Event): TimelineSpan | null {
  const open = parseDate(event.dates?.sales?.open ?? null)
  const close = parseDate(event.dates?.sales?.close ?? null)

  if (!open || !close || close <= open) return null

  const dates = sorted
    .flatMap(batch => [batch.starts_at, batch.ends_at, batch.advanced_at])
    .map(parseDate)
    .filter((date): date is DateTime => date !== null)

  return { start: DateTime.min(open, ...dates), end: DateTime.max(close, ...dates) }
}

function layout(sorted: TicketBatch[], track: TimelineSpan, now: DateTime): TimelineBar[] {
  const bars: TimelineBar[] = []

  sorted.forEach((batch, index) => {
    const configured = configuredPeriod(batch)

    const start = startOf(configured, sorted, index, track)
    const end = endOf(batch, configured, sorted, index, now)

    bars.push({
      batch,
      start,
      end,
      ghost: ghostFor(configured, start, end),
      openEnded: end === null,
      future: isFuture(batch, start, now),
    })
  })

  return bars
}

/**
 * The instant a batch certainly stopped selling, or null while that is unknown.
 * A sell-out is exact; a dated batch closes at its `ends_at`; an undated one
 * that has not advanced could still be selling.
 */
function certainEnd(batch: TicketBatch): DateTime | null {
  return parseDate(batch.advanced_at) ?? (isDated(batch) ? parseDate(batch.ends_at) : null)
}

function startOf(
  configured: TimelineSpan | null,
  sorted: TicketBatch[],
  index: number,
  track: TimelineSpan,
): DateTime {
  if (configured) {
    // Only a predecessor's sell-out opens a dated batch ahead of its own period.
    const early = parseDate(sorted[index - 1]?.advanced_at ?? null)

    return early && early < configured.start ? early : configured.start
  }

  // Undated: it opens at the last boundary anyone can point to. Predecessors
  // that have not closed yet give none, so their successors share their span
  // rather than inventing a handover that has not happened.
  for (let i = index - 1; i >= 0; i--) {
    const closed = certainEnd(sorted[i])

    if (closed) return closed
  }

  return track.start
}

function endOf(
  batch: TicketBatch,
  configured: TimelineSpan | null,
  sorted: TicketBatch[],
  index: number,
  now: DateTime,
): DateTime | null {
  const advanced = parseDate(batch.advanced_at)

  if (advanced) return advanced

  if (configured) return configured.end

  // Undated from here. A later dated batch ends this one, whether it already
  // did (supersession) or is still scheduled (the planned handover).
  const handover = sorted
    .slice(index + 1)
    .filter(isDated)
    .map(later => parseDate(later.starts_at))
    .find((date): date is DateTime => date !== null)
    ?? null

  if (handover && handover <= now) return handover

  // Undated and exhausted: it stopped at an unknown past moment, so now is the
  // only end we can assert. A handover still ahead then reads as a gap.
  if (isStalled(batch)) return now

  return handover
}

function ghostFor(
  configured: TimelineSpan | null,
  start: DateTime,
  end: DateTime | null,
): TimelineSpan | null {
  if (!configured || !end) return null

  const toPlan = start.equals(configured.start) && end.equals(configured.end)

  return toPlan ? null : configured
}

/**
 * Adjacent periods are separated by a minute (`shrinkAdjacentPeriods()` trims
 * with `subMinute()`), which is a seam rather than a gap in the sales.
 */
const GAP_FLOOR_MS = 60_000

function gapsIn(bars: TimelineBar[], track: TimelineSpan): TimelineSpan[] {
  const spans = bars
    .map(bar => ({ start: bar.start, end: bar.end ?? track.end }))
    .sort((a, b) => a.start.toMillis() - b.start.toMillis())

  const gaps: TimelineSpan[] = []
  let cursor = track.start

  for (const span of spans) {
    if (span.start > cursor) gaps.push({ start: cursor, end: span.start })
    if (span.end > cursor) cursor = span.end
  }

  if (cursor < track.end) gaps.push({ start: cursor, end: track.end })

  return gaps.filter(gap => gap.end.toMillis() - gap.start.toMillis() > GAP_FLOOR_MS)
}

function configuredPeriod(batch: TicketBatch): TimelineSpan | null {
  const start = parseDate(batch.starts_at)
  const end = parseDate(batch.ends_at)

  return start && end ? { start, end } : null
}

function isDated(batch: TicketBatch): boolean {
  return batch.starts_at !== null && batch.ends_at !== null
}

/**
 * Plan rather than history. An unresolved batch shares its predecessor's span,
 * so only its status says it has not opened.
 */
function isFuture(batch: TicketBatch, start: DateTime, now: DateTime): boolean {
  if (batch.status === 'scheduled') return true

  return start > now
}

/** Sold out with nothing written: the channel stopped here and did not advance. */
function isStalled(batch: TicketBatch): boolean {
  return batch.status === 'exhausted'
}
